import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { Hexagon, ImageIcon } from 'lucide-react';

import { AiGeneratedInlineMark } from '../../components/AiGeneratedContentBadge';
import { GraphNodeParticlePapStrip } from '../../components/GraphNodeParticlePapStrip';
import {
  GraphParticleTicketShell,
  RELATED_GRAPH_SIDE_STRIPS_EXTENSION_WIDTH,
  WORKSPACE_PARTICLE_CARD_WIDTH,
} from '../../components/GraphParticleTicketChrome';
import { MathTextBlock } from '../../components/MathTextBlock';
import { resolveKnowledgeParticleQaDisplay } from '../../components/KnowledgeParticleQaSections';
import { ProxyImage } from '../../components/assets/ProxyImage';
import { isParticlePapEligible } from '../../features/ttqib/papDisplay';
import type { KnowledgeParticle } from '../../types/domain';
import type { WorkspaceGraphNode } from '../../services/kpWorkspaceService';
import { safeExtractText } from '../../utils/textHelper';

export type WorkspaceGraphNodeData = {
  node: WorkspaceGraphNode;
  selected: boolean;
  particle?: KnowledgeParticle | null;
  previewUrl?: string | null;
  assetOssId?: string | null;
  fallbackSubject?: string | null;
  onParticleUpdated?: (particle: KnowledgeParticle) => void;
  onStubInteract?: () => void;
};

export function kindLabel(kind: WorkspaceGraphNode['kind']): string {
  if (kind === 'asset') return '素材';
  if (kind === 'particle') return '知识颗粒';
  return '衍生';
}

function kindTone(kind: WorkspaceGraphNode['kind']): string {
  if (kind === 'asset') return 'bg-[#EAF2FF] text-primary-strong';
  if (kind === 'particle') return 'bg-[#FFF1F1] text-[#B45309]';
  return 'bg-canvas text-muted';
}

function readNodeTitle(node: WorkspaceGraphNode, particle?: KnowledgeParticle | null): string {
  if (particle?.title) return safeExtractText(particle.title) || particle.title;
  return safeExtractText(node.title) || node.title || kindLabel(node.kind);
}

function readParticleQuestionPreview(node: WorkspaceGraphNode, particle?: KnowledgeParticle | null): string {
  const display = resolveKnowledgeParticleQaDisplay({
    questionSource: particle?.questions ?? node.question,
    answerSource: particle?.answers ?? node.answer,
    fallbackQuestionText: safeExtractText(node.subtitle) || safeExtractText(node.summary) || undefined,
  });
  const main = safeExtractText(display.questionDisplay.main);
  if (main) return main;
  const firstItem = display.questionDisplay.items[0];
  return safeExtractText(firstItem) || safeExtractText(node.subtitle) || safeExtractText(node.summary);
}

export function resolveParticlePapStripEligible(
  node: WorkspaceGraphNode,
  particle?: KnowledgeParticle | null,
  fallbackSubject?: string | null,
): boolean {
  if (node.kind !== 'particle') return false;
  const metadataSubject = typeof node.metadata?.subject === 'string' ? node.metadata.subject : null;
  return isParticlePapEligible({
    subject: particle?.subject || metadataSubject || fallbackSubject,
    questions: particle?.questions ?? node.question,
    answers: particle?.answers,
  });
}

export function WorkspaceAssetGraphNode({ data }: NodeProps<Node<WorkspaceGraphNodeData>>) {
  const { node, selected, previewUrl, assetOssId } = data;
  const title = readNodeTitle(node);
  const subtitle = safeExtractText(node.subtitle);

  return (
    <div style={{ width: data.node.kind === 'asset' ? 272 : 248 }}>
      <div
        className={
          'overflow-hidden rounded-xl border bg-surface shadow-card transition '
          + (selected ? 'border-2 border-primary-strong' : 'border-border')
        }
      >
        <div className="relative h-24 bg-[#EDF1F7]">
          {previewUrl ? (
            <ProxyImage
              rawUrl={previewUrl}
              ossId={assetOssId || undefined}
              alt={title}
              className="h-full w-full"
              imgClassName="object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-muted">
              <ImageIcon size={22} />
            </div>
          )}
        </div>
        <div className="space-y-1 px-3 py-2.5">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${kindTone(node.kind)}`}>
            {kindLabel(node.kind)}
          </span>
          <p className="line-clamp-2 text-sm font-semibold leading-snug text-ink">{title}</p>
          {subtitle ? <p className="line-clamp-1 text-xs text-muted">{subtitle}</p> : null}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="!h-2 !w-2 !border-2 !border-white !bg-[#D97B7B]" />
    </div>
  );
}

export function WorkspaceParticleGraphNode({ data }: NodeProps<Node<WorkspaceGraphNodeData>>) {
  const { node, selected, particle, fallbackSubject, onParticleUpdated } = data;
  const title = readNodeTitle(node, particle);
  const questionPreview = readParticleQuestionPreview(node, particle);
  const metadata = particle?.metadata && typeof particle.metadata === 'object'
    ? particle.metadata as Record<string, unknown>
    : (node.metadata && typeof node.metadata === 'object' ? node.metadata as Record<string, unknown> : null);
  const metadataSubject = typeof metadata?.subject === 'string' ? metadata.subject : null;
  const subject = particle?.subject || metadataSubject || fallbackSubject;
  const showPapStrip = resolveParticlePapStripEligible(node, particle, fallbackSubject);
  const particleId = particle?.id || node.particleId || node.id.replace(/^particle:/, '');
  const nodeWidth = showPapStrip
    ? WORKSPACE_PARTICLE_CARD_WIDTH + RELATED_GRAPH_SIDE_STRIPS_EXTENSION_WIDTH
    : WORKSPACE_PARTICLE_CARD_WIDTH;

  return (
    <div style={{ width: nodeWidth }}>
      <GraphParticleTicketShell selected={selected}>
        <div className="min-w-0 shrink-0 px-3 py-2.5" style={{ width: WORKSPACE_PARTICLE_CARD_WIDTH }}>
          <div className="flex items-start justify-between gap-2">
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${kindTone(node.kind)}`}>
              {kindLabel(node.kind)}
            </span>
            <AiGeneratedInlineMark />
          </div>
          <div className="mt-2 flex items-start gap-2">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#EAF2FF] text-primary">
              <Hexagon size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <MathTextBlock text={title} className="line-clamp-2 text-sm font-semibold leading-snug text-ink" />
              {questionPreview ? (
                <div className="mt-1.5 text-xs leading-relaxed text-muted">
                  <MathTextBlock text={questionPreview} className="line-clamp-3" />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {showPapStrip && particleId ? (
          <GraphNodeParticlePapStrip
            particleId={particleId}
            subject={subject}
            selected={selected}
            particleTitle={title}
            questionSource={particle?.questions ?? node.question}
            answerSource={particle?.answers ?? node.answer}
            particle={particle}
            particleMetadata={metadata}
            onParticleUpdated={onParticleUpdated}
            onStubInteract={data.onStubInteract}
          />
        ) : null}
      </GraphParticleTicketShell>
      <Handle type="target" position={Position.Left} className="!h-2 !w-2 !border-2 !border-white !bg-[#D97B7B]" />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-2 !w-2 !border-2 !border-white !bg-primary"
      />
    </div>
  );
}

export function WorkspaceDerivedGraphNode({ data }: NodeProps<Node<WorkspaceGraphNodeData>>) {
  const { node, selected, particle } = data;
  const title = readNodeTitle(node, particle);
  const preview = safeExtractText(node.question) || safeExtractText(node.summary) || safeExtractText(node.subtitle);

  return (
    <div style={{ width: 288 }}>
      <div
        className={
          'rounded-xl border bg-surface px-3 py-2.5 shadow-card transition '
          + (selected ? 'border-2 border-primary-strong' : 'border-border')
        }
      >
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${kindTone(node.kind)}`}>
          {kindLabel(node.kind)}
        </span>
        <p className="mt-2 line-clamp-2 text-sm font-semibold leading-snug text-ink">{title}</p>
        {preview ? (
          <div className="mt-1.5 text-xs leading-relaxed text-muted">
            <MathTextBlock text={preview} className="line-clamp-3" />
          </div>
        ) : null}
      </div>
      <Handle type="target" position={Position.Left} className="!h-2 !w-2 !border-2 !border-white !bg-primary" />
      <Handle type="source" position={Position.Right} className="!h-2 !w-2 !border-2 !border-white !bg-primary" />
    </div>
  );
}

export const workspaceGraphNodeTypes = {
  workspaceAsset: WorkspaceAssetGraphNode,
  workspaceParticle: WorkspaceParticleGraphNode,
  workspaceDerived: WorkspaceDerivedGraphNode,
};
